import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge, Info, Refresh, Search, Table } from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type { SaleWebhookInbox } from '../../domain/entities/sales.entity';
import {
  ErrorState,
  ErrorText,
  externalIdFromIdempotenceSource,
  formatDateTime,
  getApiErrorMessage,
  Mono,
  webhookEventTypeLabel,
  WebhookInboxStatusBadge,
} from '../shared/display';
import {
  useForceProcessSaleWebhookInbox,
  useSaleWebhookInbox,
} from '../hooks/use-sale-webhook-inbox';
import { useProviderAccounts } from '../hooks/use-provider-accounts';
import {
  countActiveSaleWebhookInboxFilters,
  EMPTY_SALE_WEBHOOK_INBOX_FILTERS,
  type SaleWebhookInboxFilters,
} from '../shared/sale-webhook-inbox-filters';
import { SaleWebhookInboxFiltersModal } from '../components/SaleWebhookInboxFiltersModal';
import { ConfirmModal } from '../components/ConfirmModal';
import { JsonPayloadModal } from '../components/JsonPayloadModal';
import { CorrelatedEntityEventsModal } from '../components/CorrelatedEntityEventsModal';
import { Tooltip } from '@/shared/components/tooltip';
import {
  readPageParam,
  writePageParam,
} from '@/shared/hooks/use-page-query-param';
import {
  readFiltersParams,
  writeFiltersParams,
} from '@/shared/hooks/use-url-filters';

const PER_PAGE = 10;

function formatRelativeMinutes(timestamp: number): string {
  if (!timestamp) return '';
  const minutes = Math.floor((Date.now() - timestamp) / 60_000);
  if (minutes < 1) return 'agora mesmo';
  return minutes === 1 ? 'há 1 min' : `há ${minutes} min`;
}

export function SaleWebhookInboxPage() {
  // Página e filtros vivem na URL (não em useState) — ver
  // primum-core-root/docs/plans/done/admin-dashboard-pagination-url-sync.md. Aplicar/limpar filtro
  // também reseta a página; as duas mudanças vão num único setSearchParams
  // (ver aviso em use-page-query-param.ts). Sem navegação pra rota de
  // detalhe (o payload abre em modal), mas migra igual por consistência.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = readPageParam(searchParams);
  const filters = readFiltersParams(
    searchParams,
    EMPTY_SALE_WEBHOOK_INBOX_FILTERS,
  );
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [viewingPayload, setViewingPayload] = useState<SaleWebhookInbox | null>(
    null,
  );
  const [viewingCorrelatedEventsFor, setViewingCorrelatedEventsFor] =
    useState<SaleWebhookInbox | null>(null);
  const [forcing, setForcing] = useState<SaleWebhookInbox | null>(null);
  const [, setTick] = useState(0);

  const {
    data,
    isLoading,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useSaleWebhookInbox({
    page,
    perPage: PER_PAGE,
    status: filters.status || undefined,
    eventType: filters.eventType || undefined,
    providerAccountId: filters.providerAccountId || undefined,
    idempotenceSource: filters.idempotenceSource || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
  });
  const { data: accounts } = useProviderAccounts();
  const forceProcess = useForceProcessSaleWebhookInbox();

  // Recalcula "última atualização há X min" periodicamente sem refetch.
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(interval);
  }, []);

  const accountLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const account of accounts ?? []) {
      map.set(account.id, account.displayName);
    }
    return map;
  }, [accounts]);

  const rows = useMemo(() => data?.items ?? [], [data?.items]);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PER_PAGE)) : 1;
  const activeFilterCount = countActiveSaleWebhookInboxFilters(filters);

  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writePageParam(params, next);
        return params;
      },
      { replace: true },
    );
  };

  const setFiltersAndResetPage = (next: SaleWebhookInboxFilters) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writeFiltersParams(params, next, EMPTY_SALE_WEBHOOK_INBOX_FILTERS);
        writePageParam(params, 1);
        return params;
      },
      { replace: true },
    );
  };

  const handleApplyFilters = (next: SaleWebhookInboxFilters) => {
    setFiltersAndResetPage(next);
  };

  const handleClearFilters = () => {
    setFiltersAndResetPage(EMPTY_SALE_WEBHOOK_INBOX_FILTERS);
  };

  const handleConfirmForce = () => {
    if (!forcing) return;
    forceProcess.mutate(
      { id: forcing.id, body: { reason: 'Retry manual pelo admin' } },
      { onSuccess: () => setForcing(null) },
    );
  };

  const columns: ColumnDef<SaleWebhookInbox>[] = [
    {
      key: 'createdAt',
      header: 'Data',
      render: (row) => (
        <span className="text-(--color-text-secondary) text-sm">
          {formatDateTime(row.createdAt)}
        </span>
      ),
    },
    {
      key: 'providerAccountId',
      header: 'Conta',
      render: (row) => (
        <span className="text-(--color-text-primary) text-sm">
          {accountLabelById.get(row.providerAccountId) ?? (
            <Mono>{row.providerAccountId}</Mono>
          )}
        </span>
      ),
    },
    {
      key: 'eventType',
      header: 'Tipo',
      render: (row) => (
        <Badge variant={row.eventType === 'subscription' ? 'info' : 'default'}>
          {webhookEventTypeLabel(row.eventType)}
        </Badge>
      ),
    },
    {
      key: 'idempotenceSource',
      header: 'Origem',
      render: (row) => {
        const externalId = externalIdFromIdempotenceSource(
          row.idempotenceSource,
        );
        return (
          <Tooltip
            message={row.idempotenceSource}
            trigger="hover"
            triggerClassName="block max-w-64 truncate"
          >
            <Mono>{externalId}</Mono>
          </Tooltip>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <WebhookInboxStatusBadge
          status={row.status}
          statusReason={row.statusReason}
          statusDetail={row.statusDetail}
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <div className="flex justify-end gap-1">
          <Tooltip message="Ver detalhes" trigger="hover">
            <button
              type="button"
              onClick={() => setViewingPayload(row)}
              aria-label="Ver detalhes"
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-(--color-bg-secondary) rounded-md p-1.5 transition"
            >
              <Search size="sm" />
            </button>
          </Tooltip>
          <Tooltip message="Ver eventos correlacionados" trigger="hover">
            <button
              type="button"
              onClick={() => setViewingCorrelatedEventsFor(row)}
              aria-label="Ver eventos correlacionados"
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-(--color-bg-secondary) rounded-md p-1.5 transition"
            >
              <Info size="sm" />
            </button>
          </Tooltip>
          <Tooltip message="Forçar reprocessamento" trigger="hover">
            <button
              type="button"
              onClick={() => {
                forceProcess.reset();
                setForcing(row);
              }}
              aria-label="Forçar reprocessamento"
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-(--color-bg-secondary) rounded-md p-1.5 transition"
            >
              <Refresh size="sm" />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-(--color-text-primary) text-2xl font-semibold">
            Vendas · Webhook Inbox
          </h1>
          <p className="text-(--color-text-secondary) mt-1 max-w-3xl text-sm">
            Log de webhooks recebidos dos gateways de pagamento. Encontre rows
            travadas (ex.: <Mono>failed</Mono>) e force o reprocessamento
            individual.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void refetch()}
              disabled={isFetching}
              className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary) rounded-lg border px-5 py-2.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isFetching ? 'Atualizando…' : 'Atualizar'}
            </button>
            {dataUpdatedAt > 0 && (
              <span className="text-(--color-text-secondary) text-xs">
                última atualização {formatRelativeMinutes(dataUpdatedAt)}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setIsFiltersOpen(true)}
            className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary) rounded-lg border px-5 py-2.5 text-sm font-medium transition"
          >
            Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
        </div>
      </div>

      <SaleWebhookInboxFiltersModal
        open={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        filters={filters}
        onApply={handleApplyFilters}
      />

      <JsonPayloadModal
        open={!!viewingPayload}
        onClose={() => setViewingPayload(null)}
        row={viewingPayload}
      />

      <CorrelatedEntityEventsModal
        open={!!viewingCorrelatedEventsFor}
        onClose={() => setViewingCorrelatedEventsFor(null)}
        correlationId={viewingCorrelatedEventsFor?.id}
      />

      <ConfirmModal
        open={!!forcing}
        onClose={() => setForcing(null)}
        onConfirm={handleConfirmForce}
        title="Forçar reprocessamento"
        description={
          forcing
            ? `Reprocessar a row de webhook inbox ${forcing.id}, ignorando o status atual (${forcing.status})?`
            : undefined
        }
        confirmLabel="Forçar reprocessamento"
        loading={forceProcess.isPending}
        error={forceProcess.error}
      />

      <div className="border-(--color-border) bg-(--color-bg-primary) space-y-5 rounded-xl border p-2 sm:p-6">
        {activeFilterCount > 0 && (
          <div className="flex items-center justify-between gap-3 text-xs">
            <p className="text-(--color-text-secondary)">
              {activeFilterCount === 1
                ? '1 filtro aplicado'
                : `${activeFilterCount} filtros aplicados`}
            </p>
            <button
              onClick={handleClearFilters}
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) underline"
            >
              Limpar filtros
            </button>
          </div>
        )}

        {forceProcess.isError && !forcing && (
          <ErrorText>
            {getApiErrorMessage(
              forceProcess.error,
              'Falha ao forçar reprocessamento',
            )}
          </ErrorText>
        )}

        {/* Sem isto a falha da consulta aparecia como "Nenhum webhook encontrado." */}
        {isError ? (
          <ErrorState
            message={getApiErrorMessage(
              error,
              'Não foi possível carregar os webhooks.',
            )}
            onRetry={() => void refetch()}
          />
        ) : (
          <Table
            columns={columns}
            data={rows}
            keyExtractor={(row) => row.id}
            loading={isLoading}
            emptyMessage="Nenhum webhook encontrado."
            striped
          />
        )}

        <div className="flex items-center justify-between">
          <p className="text-(--color-text-secondary) text-xs">
            {data
              ? `${data.total} ${data.total === 1 ? 'resultado encontrado' : 'resultados encontrados'}`
              : null}
          </p>

          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || isLoading}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                Anterior
              </button>
              <span className="text-(--color-text-secondary) text-xs">
                {page} de {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || isLoading}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
